import React, { useEffect, useState } from 'react';
import { supabase, auth } from '../config/supabase';
import { getLocalRecoverySetupHint } from '../utils/authRedirect';
import { formatAuthError, resolveUserAccess, LOGIN_MESSAGE_KEY } from '../utils/userAccess';
import './auth/Auth.css';

interface LoginProps {
  onLoginSuccess: () => void;
}

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  useEffect(() => {
    const stored = sessionStorage.getItem(LOGIN_MESSAGE_KEY);
    if (stored) {
      setError(stored);
      sessionStorage.removeItem(LOGIN_MESSAGE_KEY);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setInfo('');

    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        setError(formatAuthError(signInError.message));
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const access = await resolveUserAccess(user);
        if (!access.userActive) {
          await auth.signOut();
          setError('Tu cuenta está deshabilitada. Contactá al administrador de BorisMetrics.');
          return;
        }
      }

      onLoginSuccess();
    } catch {
      setError('Ocurrió un error inesperado. Intentá de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Ingresá tu email para enviar el enlace de recuperación.');
      return;
    }

    setIsLoading(true);
    setError('');
    setInfo('');

    try {
      const { error: resetError, redirectTo } = await auth.resetPassword(email.trim());
      if (resetError) {
        setError(formatAuthError(resetError.message));
      } else {
        const hint =
          process.env.NODE_ENV === 'development'
            ? ` ${getLocalRecoverySetupHint(redirectTo)}`
            : '';
        setInfo(`Te enviamos un enlace para restablecer tu contraseña. Revisá tu bandeja de entrada.${hint}`);
        setShowForgotPassword(false);
      }
    } catch {
      setError('Ocurrió un error inesperado. Intentá de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  if (showForgotPassword) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-header">
            <h1 className="auth-brand">BorisMetrics</h1>
            <p className="auth-subtitle">Recuperar contraseña</p>
          </div>

          <form onSubmit={handleForgotPassword}>
            <div className="auth-field">
              <label className="auth-label" htmlFor="forgot-email">
                Email
              </label>
              <input
                id="forgot-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                required
                className="auth-input"
              />
            </div>

            {error && <div className="auth-alert auth-alert--error">{error}</div>}
            {info && <div className="auth-alert auth-alert--success">{info}</div>}

            <button type="submit" disabled={isLoading} className="auth-btn auth-btn--primary">
              {isLoading ? 'Enviando...' : 'Enviar enlace de recuperación'}
            </button>

            <button
              type="button"
              onClick={() => {
                setShowForgotPassword(false);
                setError('');
              }}
              disabled={isLoading}
              className="auth-btn auth-btn--outline"
            >
              Volver al inicio de sesión
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-header">
          <h1 className="auth-brand">BorisMetrics</h1>
          <p className="auth-subtitle">Iniciá sesión para continuar</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="auth-field">
            <label className="auth-label" htmlFor="login-email">
              Email
            </label>
            <input
              id="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@email.com"
              required
              className="auth-input"
            />
          </div>

          <div className="auth-field auth-field--tight">
            <label className="auth-label" htmlFor="login-password">
              Contraseña
            </label>
            <input
              id="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Tu contraseña"
              required
              className="auth-input"
            />
          </div>

          <div className="auth-link-row">
            <button
              type="button"
              onClick={() => {
                setShowForgotPassword(true);
                setError('');
                setInfo('');
              }}
              className="auth-link"
            >
              ¿Olvidaste tu contraseña?
            </button>
          </div>

          {error && <div className="auth-alert auth-alert--error">{error}</div>}
          {info && <div className="auth-alert auth-alert--success">{info}</div>}

          <button type="submit" disabled={isLoading} className="auth-btn auth-btn--primary">
            {isLoading ? 'Iniciando sesión...' : 'Iniciar sesión'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
